<template>
  <div :class="{ 'd-none': hidden }">
    <FormFieldsRow
      v-for="(method, index) in instructionalMethods"
      :key="index"
      :index="index"
      :isEditing="editable"
      class="mb-4 px-2 py-3"
    >
      <FormInstructionalMethod
        :value="method"
        :editable="editable"
        :config="config"
        :disabled="disabled"
        :iteration="iteration"
        :index="index"
        :formValue="formValue"
        :nestedMode="nestedMode"
        @input="updateItem(index, $event)"
        @delete="deleteItem(index)"
      />
    </FormFieldsRow>
    <div
      v-if="!instructionalMethods || !instructionalMethods.length"
      class="p-3 mb-2 text-muted bg-light rounded-5 text-center border"
    >
      <span>No Instructional Methods Added</span>
    </div>
    <AppButton
      v-if="!disabled"
      @click.stop="addItem"
    >
      Add New Instructional Method
    </AppButton>
  </div>
</template>

<script>
import _uniqBy from 'lodash/uniqBy'
import FormMixin from '@/components/Forms/FormMixin'
import FormFieldsRow from '@/components/Forms/FormFieldsRow'
import FormInstructionalMethod from '@/components/Forms/FormInstructionalMethod'
import useFormListWrapper from './composables/useFormListWrapper'
import { useVuelidate } from '@vuelidate/core'
import { computed } from '@vue/composition-api'
import { INTEGRATION_ETHOS_INSTRUCTIONAL_METHOD_ID_KEY } from '@shared/constants/featureFlags'
import useFeatureFlags from '@/components/composables/useFeatureFlags'
import { generateToken } from '@shared/utils/common'

export default {
  mixins: [ FormMixin ],
  components: {
    FormFieldsRow,
    FormInstructionalMethod,
  },
  props: {
    editable: {
      type: Boolean,
      default: true,
    },
    formValue: {
      type: Object,
      default: () => ({}),
    },
    iteration: {
      type: Number,
      default: 0,
    },
    nestedMode: {
      type: Boolean,
      default: true,
    },
  },
  setup (props, { emit }) {
    const { integrationKeyIMByID } = useFeatureFlags({
      integrationKeyIMByID: INTEGRATION_ETHOS_INSTRUCTIONAL_METHOD_ID_KEY,
    })
    function createEmptyItem () {
      if (integrationKeyIMByID) {
        return {
          id: '', // NOTE: a unique token is no longer required as this field will now be set by user
        }
      }
      return {
        code: '',
        name: '',
        id: generateToken(20),
      }
    }
    const {
      items,
      updateItem,
      addItem,
      deleteItem,
    } = useFormListWrapper({ props, emit, createEmptyItem })

    const ids = computed(() => (items.value || []).map(v => v.ids))
    const codes = computed(() => (items.value || []).map(v => v.code))

    // NOTE: once we remove the FF from the application, this variable should be
    // renamed to isInstructionalMethodsIdUnique to be more descriptive
    const isInstructionalMethodsUnique = integrationKeyIMByID
      ? computed(() => _uniqBy(items.value, 'id').length === ids.value.length)
      : computed(() => _uniqBy(items.value, 'code').length === codes.value.length)

    const $v = useVuelidate({
      value: {
        isInstructionalMethodsCodeUnique: {
          $validator: () => isInstructionalMethodsUnique.value,
          $message: () => 'Instructional Methods should be unique',
          $params: {
            ids,
          },
        },
      },
    }, { value: items })

    return {
      instructionalMethods: items,
      updateItem,
      addItem,
      deleteItem,
      $v,
    }
  },
}
</script>
